import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { adminService } from '../../services/adminService';
import { X, BookOpen, Loader2, Plus } from 'lucide-react';

interface AddSubjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddSubjectModal: React.FC<AddSubjectModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    is_archived: false
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};
    if (!formData.name.trim()) {
      newErrors.name = "Subject name is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const addSubjectMutation = useMutation({
    mutationFn: adminService.createSubject,
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (error) => {
      setErrors({ submit: error.toString() });
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      addSubjectMutation.mutate(formData);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-md bg-slate-800/95 backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="relative p-6 border-b border-white/10">
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full opacity-20 blur-3xl" />
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Add New Subject</h2>
                <p className="text-xs text-slate-400">Create a new subject for learning</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Subject Name */}
          <div>
            <label className="block text-sm text-slate-400 mb-2">Subject Name</label>
            <div className="relative">
              <BookOpen className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text"
                placeholder="e.g., Mathematics Form 3"
                value={formData.name}
                onChange={e => setFormData({...formData, name: e.target.value})}
                className={`w-full pl-10 pr-4 py-3 bg-white/5 border rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none transition-colors ${
                  errors.name ? 'border-red-500/50' : 'border-white/10 focus:border-amber-500/50'
                }`}
              />
            </div>
            {errors.name && (
              <p className="text-red-400 text-xs mt-2 ml-1">{errors.name}</p>
            )}
          </div>

          {/* Status Toggle */}
          <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10">
            <div>
              <p className="text-sm font-medium text-white">Active Status</p>
              <p className="text-xs text-slate-400">Subject will be available for enrollment</p>
            </div>
            <button
              type="button"
              onClick={() => setFormData({...formData, is_archived: !formData.is_archived})}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                !formData.is_archived ? 'bg-emerald-500' : 'bg-slate-600'
              }`}
            >
              <span 
                className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                  !formData.is_archived ? 'left-7' : 'left-1'
                }`}
              />
            </button>
          </div>

          {errors.submit && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              {errors.submit}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-slate-300 hover:bg-white/10 transition-colors text-sm font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={addSubjectMutation.isPending}
              className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:opacity-90 transition-opacity text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {addSubjectMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Add Subject
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddSubjectModal;
